import axios from "axios";
import { Col, DatePicker, Pagination, Row, Table, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import moment from "moment";
import { useMemo, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { getAuthTokens, getTenantId, selectCurrentUser } from "../../../redux/features/auth/authSlice";
import { apiRoutes, baseURL } from "../../../config/api";
import { useGetTrackNTraceTagEpcisQuery } from "../../../redux/services/trackntraceApiSlice";
import { useGetAllTenantProcessQuery } from "../../../redux/services/processApiSlice";
import { store } from "../../../redux/store";
import { removeEmptyKeys } from "../../../utils";
import ReportFiltersCard from "./ReportFiltersCard";
import { REPORT_TABLE_BODY_MAX_PX } from "./reportTableLayout";
import { exportRowsToWorkbook } from "./exportWorkbook";


const { RangePicker } = DatePicker;

const extractEpcToken = (value: unknown): string => {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value).trim();
  if (typeof value === "object") {
    const candidate = value as {
      epc?: unknown;
      EPC?: unknown;
      id?: unknown;
      value?: unknown;
      serial?: unknown;
      serialNumber?: unknown;
    };
    return String(
      candidate.epc ??
        candidate.EPC ??
        candidate.value ??
        candidate.id ??
        candidate.serial ??
        candidate.serialNumber ??
        ""
    ).trim();
  }
  return "";
};

const mapRow = (row: Record<string, unknown>, fallbackUserName = "") => {
  const status = String(row.status ?? "").toLowerCase();
  const isSuccess = status === "enabled" || status === "success";
  const processValue =
    (row.processName as string) ??
    ((row.process as { name?: string } | undefined)?.name as string) ??
    (row.processId as string) ??
    "";
  
  // Extract EPC from various possible fields
  let epcValue = "";
  if (row.epc) {
    epcValue = String(row.epc);
  } else if (row.EPC) {
    epcValue = String(row.EPC);
  } else if (typeof row.epcList === "string" || typeof row.epcList === "number") {
    epcValue = extractEpcToken(row.epcList);
  } else if (Array.isArray(row.epcList) && row.epcList.length > 0) {
    epcValue = extractEpcToken(row.epcList[0]);
  } else if (typeof row.epcisList === "string" || typeof row.epcisList === "number") {
    epcValue = extractEpcToken(row.epcisList);
  } else if (Array.isArray(row.epcisList) && row.epcisList.length > 0) {
    epcValue = extractEpcToken(row.epcisList[0]);
  }
  
  // Extract product description from various possible fields
  const productDescription = 
    (row.productDescription as string) ?? 
    (row.description as string) ?? 
    (row.productName as string) ?? 
    ((row.product as { description?: string; name?: string } | undefined)?.description) ??
    ((row.product as { description?: string; name?: string } | undefined)?.name) ??
    (row.itemDescription as string) ??
    (row.materialDescription as string) ??
    "";
  
  return {
    key: (row.diId ?? row.id ?? row._id ?? "") as string,
    siteName: (row.siteName ?? "") as string,
    productUPC: (row.productUPC ?? row.sku ?? "") as string,
    productDescription: productDescription,
    epc: epcValue,
    serial: (row.diId ?? epcValue ?? "") as string,
    dateWritten: (row.operationTime ?? row.createdAt ?? "") as string,
    userName: String(row.userName ?? row.username ?? "").trim() || fallbackUserName,
    zone: (row.zoneName ?? row.deviceName ?? "") as string,
    process: processValue,
    success: isSuccess ? "Yes" : "No",
  };
};

const InventoryTagEpcisHistory = () => {
  const [searchParams] = useSearchParams();
  const tagLabel = searchParams.get("tagId") ?? "";
  const diIdForApi = (searchParams.get("diId") ?? searchParams.get("tagId") ?? "").trim();
  const epcFromNav = (searchParams.get("epc") ?? "").trim();
  const descriptionFromNav = (searchParams.get("description") ?? "").trim();
  const skuFromNav = searchParams.get("sku") ?? "";
  const siteIdFromNav = (searchParams.get("siteId") ?? "").trim();
  const zoneIdFromNav = (searchParams.get("zoneId") ?? "").trim();
  const siteNameFromNav = (searchParams.get("siteName") ?? "").trim().toLowerCase();
  const zoneNameFromNav = (searchParams.get("zoneName") ?? "").trim().toLowerCase();

  const tenantId = useSelector(getTenantId);
  const sessionUser = useSelector(selectCurrentUser);
  const sessionUsername =
    (sessionUser?.username as string | undefined) ||
    (sessionUser?.userRole?.userName as string | undefined) ||
    (sessionUser?.name as string | undefined) ||
    "";

  const [dateRange, setDateRange] = useState<[moment.Moment | null, moment.Moment | null] | null>(null);
  const [appliedDateRange, setAppliedDateRange] =
    useState<[moment.Moment | null, moment.Moment | null] | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const collectRowIdentityTokens = useCallback((row: Record<string, unknown>) => {
    const tokens = new Set<string>();
    const add = (v: unknown) => {
      if (v == null) return;
      if (typeof v === "string") {
        String(v)
          .split(/[\s,;]+/)
          .map((p) => p.trim().toLowerCase())
          .filter(Boolean)
          .forEach((p) => tokens.add(p));
      } else if (Array.isArray(v)) {
        v.forEach(add);
      } else if (typeof v === "object") {
        const token = extractEpcToken(v);
        if (token) add(token);
      }
    };
    add(row.diId);
    add(row.epcList);
    add(row.epcisList);
    add(row.epc);
    add(row.EPC);
    return tokens;
  }, []);

  const matchesScopedContext = useCallback(
    (row: Record<string, unknown>) => {
      const drillIds = [diIdForApi, tagLabel, epcFromNav]
        .map((s) => String(s || "").trim().toLowerCase())
        .filter(Boolean);
      const uniqueDrillIds = drillIds.filter((id, idx) => drillIds.indexOf(id) === idx);
      if (uniqueDrillIds.length > 0) {
        const rowTokens = collectRowIdentityTokens(row);
        const rowDi = String(row.diId ?? "").trim().toLowerCase();
        if (rowDi) {
          if (!uniqueDrillIds.some((id) => id === rowDi)) return false;
        } else if (!uniqueDrillIds.some((id) => rowTokens.has(id))) {
          return false;
        }
      }

      const rowUpc = String(row.productUPC ?? row.sku ?? "").trim().toLowerCase();
      const upcNeedle = skuFromNav.trim().toLowerCase();
      if (upcNeedle && rowUpc !== upcNeedle) return false;

      const rowSiteId = String(row.siteId ?? "").trim();
      if (siteIdFromNav && rowSiteId !== siteIdFromNav) return false;

      const rowZoneId = String(row.zoneId ?? "").trim();
      if (zoneIdFromNav && rowZoneId !== zoneIdFromNav) return false;

      const rowSiteName = String(row.siteName ?? "").trim().toLowerCase();
      const rowZoneName = String(row.zoneName ?? row.deviceName ?? "").trim().toLowerCase();
      if (siteNameFromNav && rowSiteName !== siteNameFromNav) return false;
      if (zoneNameFromNav && rowZoneName !== zoneNameFromNav) return false;

      return true;
    },
    [
      diIdForApi,
      tagLabel,
      epcFromNav,
      skuFromNav,
      siteIdFromNav,
      zoneIdFromNav,
      siteNameFromNav,
      zoneNameFromNav,
      collectRowIdentityTokens,
    ]
  );

  const applyFilters = useCallback(() => {
    setAppliedDateRange(dateRange);
    setPage(1);
  }, [dateRange]);

  const resetFilters = useCallback(() => {
    setDateRange(null);
    setAppliedDateRange(null);
    setPage(1);
  }, []);

  const queryParams = useMemo(
    () =>
      removeEmptyKeys({
        page,
        limit: pageSize,
        diId: diIdForApi || undefined,
        productUPC: skuFromNav.trim() || undefined,
        siteId: siteIdFromNav || undefined,
        zoneId: zoneIdFromNav || undefined,
        tenant: tenantId || undefined,
        from: appliedDateRange?.[0] ? moment.utc(appliedDateRange[0]).format("YYYY-MM-DD") : undefined,
        to: appliedDateRange?.[1] ? moment.utc(appliedDateRange[1]).format("YYYY-MM-DD") : undefined,
      }),
    [
      page,
      pageSize,
      diIdForApi,
      skuFromNav,
      siteIdFromNav,
      zoneIdFromNav,
      tenantId,
      appliedDateRange,
    ]
  );

  const { data: processData } = useGetAllTenantProcessQuery(
    { tenantId, processType: "Digitization" },
    { skip: !tenantId }
  );
  const processList = useMemo(() => {
    const rows = processData?.result?.rows ?? processData?.data ?? [];
    return Array.isArray(rows)
      ? rows.map((p: { id: string; name?: string }) => ({ label: p.name ?? p.id, value: p.id }))
      : [];
  }, [processData]);
  const processNameMap = useMemo(
    () => Object.fromEntries(processList.map((p: { label: string; value: string }) => [p.value, p.label])),
    [processList]
  );

  const { data, isFetching } = useGetTrackNTraceTagEpcisQuery(queryParams, {
    skip: !tenantId || !diIdForApi,
  });

  const rows = useMemo(() => {
    const extractedRows = data?.data ?? data?.result?.data ?? data?.result?.rows ?? [];
    return extractedRows;
  }, [data]);

  const tableData = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    const mappedRows = rows
      .filter((row) => matchesScopedContext(row as Record<string, unknown>))
      .map((row, index) => {
      const mapped = mapRow(row as Record<string, unknown>, sessionUsername);
      const processIdFromRow = String(
        (row as Record<string, unknown>).processId ??
          ((row as Record<string, unknown>).process as { id?: string } | undefined)?.id ??
          ""
      );
      return {
        ...mapped,
        tagId: tagLabel || diIdForApi || mapped.serial || "-",
        key: mapped.key || `epcis-${index}-${mapped.dateWritten}`,
        process: mapped.process || processNameMap[processIdFromRow] || processIdFromRow || "-",
      };
    });

    return mappedRows.sort((a, b) => {
      const aTime = a.dateWritten ? moment(a.dateWritten).valueOf() : 0;
      const bTime = b.dateWritten ? moment(b.dateWritten).valueOf() : 0;
      return bTime - aTime;
    });
  }, [rows, matchesScopedContext, processNameMap, tagLabel, diIdForApi, sessionUsername]);

  const serverPaginationTotal =
    data?.pagination?.total ?? data?.result?.pagination?.total ?? rows.length ?? 0;
  const totalCount = useMemo(() => {
    if (!Array.isArray(rows) || rows.length === 0) return serverPaginationTotal;
    const filteredLen = tableData.length;
    if (filteredLen < rows.length) {
      return filteredLen;
    }
    return serverPaginationTotal;
  }, [rows, tableData.length, serverPaginationTotal]);

  const columns: ColumnsType<(typeof tableData)[number]> = useMemo(
    () => [
      {
        title: "Tag ID",
        dataIndex: "tagId",
        key: "tagId",
        width: 150,
        sorter: (a, b) => String(a.tagId ?? "").localeCompare(String(b.tagId ?? "")),
      },
      {
        title: "UPC",
        dataIndex: "productUPC",
        key: "productUPC",
        width: 110,
        sorter: (a, b) => String(a.productUPC ?? "").localeCompare(String(b.productUPC ?? "")),
      },
      {
        title: "Product description",
        dataIndex: "productDescription",
        key: "productDescription",
        width: 170,
        ellipsis: true,
        sorter: (a, b) => String(a.productDescription ?? "").localeCompare(String(b.productDescription ?? "")),
        render: (v: string) => (v && String(v).trim() ? v : descriptionFromNav || "—"),
      },
      {
        title: "EPC",
        dataIndex: "epc",
        key: "epc",
        width: 160,
        sorter: (a, b) => String(a.epc ?? "").localeCompare(String(b.epc ?? "")),
        render: (v: string) => v || "-",
      },
      {
        title: "Site",
        dataIndex: "siteName",
        key: "siteName",
        width: 120,
        sorter: (a, b) => String(a.siteName ?? "").localeCompare(String(b.siteName ?? "")),
      },
      {
        title: "Zone",
        dataIndex: "zone",
        key: "zone",
        width: 120,
        sorter: (a, b) => String(a.zone ?? "").localeCompare(String(b.zone ?? "")),
      },
      {
        title: "Process",
        dataIndex: "process",
        key: "process",
        width: 130,
        sorter: (a, b) => String(a.process ?? "").localeCompare(String(b.process ?? "")),
      },
      {
        title: "Username",
        dataIndex: "userName",
        key: "userName",
        width: 110,
        sorter: (a, b) => String(a.userName ?? "").localeCompare(String(b.userName ?? "")),
        render: (v: string) => (v && String(v).trim() ? v : "—"),
      },
      {
        title: "Time",
        dataIndex: "dateWritten",
        key: "dateWritten",
        width: 165,
        fixed: "right",
        sorter: (a, b) => {
          const aTime = a.dateWritten ? moment(a.dateWritten).valueOf() : 0;
          const bTime = b.dateWritten ? moment(b.dateWritten).valueOf() : 0;
          return aTime - bTime;
        },
        render: (val: string) => (val ? moment(val).format("DD MMM YY hh:mm A") : "-"),
      },
    ],
    [descriptionFromNav]
  );

  const getExportParams = useCallback(
    () =>
      removeEmptyKeys({
        diId: diIdForApi || undefined,
        productUPC: skuFromNav.trim() || undefined,
        siteId: siteIdFromNav || undefined,
        zoneId: zoneIdFromNav || undefined,
        tenant: tenantId || undefined,
        page: 1,
        limit: 10000,
        from: appliedDateRange?.[0] ? moment.utc(appliedDateRange[0]).format("YYYY-MM-DD") : undefined,
        to: appliedDateRange?.[1] ? moment.utc(appliedDateRange[1]).format("YYYY-MM-DD") : undefined,
      }),
    [
      diIdForApi,
      skuFromNav,
      siteIdFromNav,
      zoneIdFromNav,
      tenantId,
      appliedDateRange,
    ]
  );

  const fetchDataForExport = useCallback((params: Record<string, unknown>) => {
    const st = store.getState();
    const tokens = getAuthTokens(st) as {
      idToken?: string;
      accessToken?: string;
      refreshToken?: string;
    };
    const tid = getTenantId(st);
    return axios.get(`${baseURL}${apiRoutes.EPCIS_HISTORY}`, {
      params,
      headers: {
        "id-token": tokens?.idToken ?? "",
        "access-token": tokens?.accessToken ?? "",
        "refresh-token": tokens?.refreshToken ?? "",
        ...(tid ? { "tenant-id": tid } : {}),
      },
    });
  }, []);

  const exportCsv = useCallback(() => {
    if (!tenantId) {
      message.warning("No tenant selected.");
      return;
    }
    fetchDataForExport(getExportParams())
      .then((res) => {
        const rows = res?.data?.data ?? res?.data?.result?.data ?? res?.data?.result?.rows ?? [];
        const arr = Array.isArray(rows)
          ? rows
              .filter((row) => matchesScopedContext(row as Record<string, unknown>))
              .map((row, index) => {
              const mapped = mapRow(row as Record<string, unknown>, sessionUsername);
              const processIdFromRow = String(
                (row as Record<string, unknown>).processId ??
                  ((row as Record<string, unknown>).process as { id?: string } | undefined)?.id ??
                  ""
              );
              return {
                ...mapped,
                tagId: tagLabel || diIdForApi || mapped.serial || "-",
                key: mapped.key || `epcis-${index}-${mapped.dateWritten}`,
                process: mapped.process || processNameMap[processIdFromRow] || processIdFromRow || "-",
              };
            })
          : [];
        const header = ["Tag ID", "UPC", "Product description", "EPC", "Site", "Zone", "Process", "Username", "Time"];
        const csvContent = [
          header.join(","),
          ...arr.map((r) =>
            [
              r.tagId,
              r.productUPC,
              r.productDescription,
              r.epc,
              r.siteName,
              r.zone,
              r.process,
              r.userName,
              r.dateWritten ? moment(r.dateWritten).format("DD MMM YY hh:mm A") : "",
            ]
              .map((c) => `"${String(c).replace(/"/g, '""')}"`)
              .join(",")
          ),
        ].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `inventory-tag-epcis-${moment().format("YYYY-MM-DD")}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        message.success("CSV exported.");
      })
      .catch(() => message.error("Export failed."));
  }, [tenantId, fetchDataForExport, getExportParams, tagLabel, diIdForApi, processNameMap, sessionUsername, matchesScopedContext]);

  const exportExcel = useCallback(() => {
    if (!tenantId) {
      message.warning("No tenant selected.");
      return;
    }
    fetchDataForExport(getExportParams())
      .then((res) => {
        const rows = res?.data?.data ?? res?.data?.result?.data ?? res?.data?.result?.rows ?? [];
        const arr = Array.isArray(rows)
          ? rows
              .filter((row) => matchesScopedContext(row as Record<string, unknown>))
              .map((row, index) => {
              const mapped = mapRow(row as Record<string, unknown>, sessionUsername);
              const processIdFromRow = String(
                (row as Record<string, unknown>).processId ??
                  ((row as Record<string, unknown>).process as { id?: string } | undefined)?.id ??
                  ""
              );
              return {
                ...mapped,
                tagId: tagLabel || diIdForApi || mapped.serial || "-",
                key: mapped.key || `epcis-${index}-${mapped.dateWritten}`,
                process: mapped.process || processNameMap[processIdFromRow] || processIdFromRow || "-",
              };
            })
          : [];
        const header = ["Tag ID", "UPC", "Product description", "EPC", "Site", "Zone", "Process", "Username", "Time"];
        const excelRows: Array<Array<string | number>> = [
          header,
          ...arr.map((r) => [
            r.tagId,
            r.productUPC,
            r.productDescription || descriptionFromNav,
            r.epc,
            r.siteName,
            r.zone,
            r.process,
            r.userName,
            r.dateWritten ? moment(r.dateWritten).format("DD MMM YY hh:mm A") : "",
          ]),
        ];
        exportRowsToWorkbook(
          `inventory-tag-epcis-${moment().format("YYYY-MM-DD")}.xlsx`,
          "Inventory Tag EPCIS",
          excelRows
        );
        message.success("Excel exported.");
      })
      .catch(() => message.error("Export failed."));
  }, [
    tenantId,
    fetchDataForExport,
    getExportParams,
    tagLabel,
    diIdForApi,
    processNameMap,
    sessionUsername,
    matchesScopedContext,
    descriptionFromNav,
  ]);

  if (!diIdForApi) {
    return (
      <div className="di-reports-page">
        <p className="details-missing-user">No tag selected. Open this page from Inventory Details.</p>
      </div>
    );
  }

  return (
    <div className="di-reports-page inventory-details-page inventory-reports-page">
      <ReportFiltersCard>
        <Row gutter={[12, 12]} align="middle" className="filters-row">
          <Col className="filter-field-col">
            <span className="filter-label">Time Range</span>
            <RangePicker
              value={dateRange ?? undefined}
              onChange={(dates) => setDateRange(dates as [moment.Moment | null, moment.Moment | null] | null)}
              allowClear
              className="filter-input-control"
            />
          </Col>
          <Col className="filters-inline-actions">
            <button type="button" className="btn-apply-filters" onClick={applyFilters}>
              Apply Filters
            </button>
            <button type="button" className="btn-reset-filters" onClick={resetFilters}>
              Reset
            </button>
          </Col>
        </Row>
      </ReportFiltersCard>

      <section className="enablement-table-section">
        <div className="table-card">
          <div className="filters-section-title">EPCIS history for tag</div>
          <Table
            columns={columns}
            dataSource={tableData}
            loading={isFetching}
            rowKey="key"
            className="uitable DITable diInfo"
            pagination={false}
            scroll={{ x: 1550, y: REPORT_TABLE_BODY_MAX_PX }}
            size="small"
          />
          <div className="reports-table-footer">
            <div className="enablement-pagination-actions inventory-details-actions">
              <button type="button" className="report-action-btn" onClick={exportCsv}>
                Export CSV
              </button>
              <button type="button" className="report-action-btn" onClick={exportExcel}>
                Export Excel
              </button>
            </div>
            <div className="pagination-with-size">
              <Pagination
                current={page}
                defaultCurrent={1}
                pageSize={pageSize}
                defaultPageSize={25}
                total={totalCount}
                hideOnSinglePage={false}
                showSizeChanger={false}
                onChange={(newPage) => {
                  setPage(newPage);
                }}
              />
              <select
                className="pagination-size-select-native"
                value={String(pageSize)}
                onChange={(event) => {
                  setPage(1);
                  setPageSize(Number(event.target.value));
                }}
              >
                <option value="25">25 / page</option>
                <option value="50">50 / page</option>
                <option value="100">100 / page</option>
              </select>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InventoryTagEpcisHistory;
